import React from "react";
import dayjs from "dayjs";
import { Modal } from "../../../ui/primitives/Modal";
import { Stack } from "../../../ui/primitives/Stack";
import { Tag } from "../../../ui/primitives/Tag";
import { Text } from "../../../ui/primitives/Text";
import { useTokens } from "../../../core/theme/TokensProvider";
import { Allocation, AllocationKind } from "../../../model/members/Rota";
import { allocationLabel } from "../../../utils/rotaUtils";

// Category wording matches the Work Allocation rota so the same piece of work isn't called
// two different things on the two screens that show it.
const CATEGORY_LABELS: Record<AllocationKind, string> = {
  Task: "Task",
  Call: "Call",
  BizDev: "Business Development",
};

const CATEGORY_TONES: Record<AllocationKind, "primary" | "warning" | "success"> = {
  Task: "primary",
  Call: "warning",
  BizDev: "success",
};

export interface WorkBriefModalProps {
  allocation: Allocation | null;
  onClose: () => void;
}

/**
 * The full brief for one allocated piece of work, laid out like an email.
 *
 * The compact work widget deliberately shows only the category and title, so a long brief
 * can't stretch it (FORT-351) — this is where the whole instruction is readable. The
 * header/body split is the point: staff scan the "subject" and category first, then read the
 * body, exactly as they would an email from a manager.
 *
 * `whiteSpace: pre-wrap` matters — a brief is typed into a textarea, so the manager's own
 * paragraph and list breaks are meaningful. Collapsing them would turn a structured brief
 * into a wall of text.
 */
const WorkBriefModal: React.FC<WorkBriefModalProps> = ({ allocation, onClose }) => {
  const tokens = useTokens();

  // Unmounted rather than closed when nothing is selected. `open` below is therefore always
  // true — kept off the element so the two can't disagree about what "open" means.
  if (!allocation) return null;

  const brief = allocation.brief?.trim();

  return (
    <Modal
      open
      title="Work brief"
      onClose={onClose}
      onOk={onClose}
      okText="Close"
      data-testid="work-brief-modal"
    >
      <Stack gap={3}>
        <Stack direction="horizontal" gap={2} align="center">
          <Tag tone={CATEGORY_TONES[allocation.kind]}>{CATEGORY_LABELS[allocation.kind]}</Tag>
          <Text color="secondary">{dayjs(allocation.date).format("ddd D MMM YYYY")}</Text>
        </Stack>

        <Text variant="h4" data-testid="work-brief-subject">
          {allocationLabel(allocation)}
        </Text>

        <Text color="secondary">Assigned to {allocation.staffName}</Text>

        <div
          data-testid="work-brief-body"
          style={{
            borderTop: `1px solid ${tokens.color.border.default}`,
            paddingTop: tokens.space[3],
            whiteSpace: "pre-wrap",
            // No line-height token exists yet; 1.6 is the readable-prose value.
            lineHeight: 1.6,
          }}
        >
          {brief ? (
            <Text>{brief}</Text>
          ) : (
            <Text color="secondary">No brief was added to this piece of work.</Text>
          )}
        </div>
      </Stack>
    </Modal>
  );
};

export default WorkBriefModal;
